import "./sentry";
import { useEffect, useMemo, useRef } from "react";
import type { JSX } from "react";

declare global {
  interface Window {
    botpressWebChat?: { sendEvent: (e: { type: string }) => void };
  }
}

function BotpressWidget() {
  const location = useLocation();

  useEffect(() => {
    const bp = window.botpressWebChat;
    if (!bp) return;
    const isHidden =
      location.pathname.startsWith("/admin") ||
      location.pathname === "/login" ||
      location.pathname === "/signup" ||
      location.pathname === "/email-verification" ||
      location.pathname === "/request-password-reset" ||
      location.pathname === "/password-reset";
    bp.sendEvent({ type: isHidden ? "hide" : "show" });
  }, [location.pathname]);

  // COULEUR DU BOUTON AUX COULEURS DU THÈME.
  //
  // Le lanceur Botpress vit dans un SHADOW DOM (#fab-root) : la surcharge de
  // Main.css (.bpFab…) n'y entre pas, d'où un bouton teal sur tous les thèmes.
  // On injecte donc la règle DANS le shadow root. Elle lit les variables du thème
  // (`--primary`), qui, elles, traversent la frontière : un changement de thème
  // recolore le bouton sans rien réinjecter. Le widget pouvant se (re)monter après
  // nous, on vérifie périodiquement que la règle est toujours là.
  useEffect(() => {
    const ensure = () => {
      const root = document.getElementById("fab-root")?.shadowRoot;
      if (!root || root.getElementById("edgely-fab-theme")) return;
      const style = document.createElement("style");
      style.id = "edgely-fab-theme";
      style.textContent =
        ".bpFabContainer{background-color:hsl(var(--primary))!important;box-shadow:0 4px 14px hsl(var(--primary)/.35)!important}" +
        ".bpFabIcon{background-color:hsl(var(--primary-foreground))!important}";
      root.appendChild(style);
    };
    ensure();
    const id = window.setInterval(ensure, 2000);
    return () => window.clearInterval(id);
  }, []);

  return null;
}
import { HelmetProvider } from "react-helmet-async";
import { Outlet, useLocation } from "react-router-dom";
import { routes } from "wasp/client/router";
import { api } from "wasp/client/api";
import { useAuth } from "wasp/client/auth";
import { getCurrentUserRole, getMyAffiliateProfile, syncMyAdminStatus, useQuery } from "wasp/client/operations";
import { Toaster } from "../client/components/ui/toaster";
import "./Main.css";
import NavBar from "./components/NavBar/NavBar";
import {
  getDemoNavigationItems,
  getMarketingNavigationItems,
} from "./components/NavBar/constants";
import { useCustomPages } from "./components/NavBar/useCustomPages";
import { isMarketingPath } from "./hooks/useIsMarketingPage";
import useColorMode, { applyThemeClasses } from "./hooks/useColorMode";
import { useUiMode } from "../lite/mode/useUiMode";
import { LiteShell } from "../lite/shell/LiteShell";
import PrimaryRail from "./components/Navigation2/PrimaryRail";
import SecondarySidebar from "./components/Navigation2/SecondarySidebar";
import MobileNavigation2 from "./components/Navigation2/MobileNavigation2";
import AddCustomPageButton from "./components/Navigation2/AddCustomPageButton";
import CookieConsentBanner from "./components/cookie-consent/Banner";
import { LanguageProvider } from "./contexts/LanguageContext";
import useTranslation from "./hooks/useTranslation";
import OnboardingModal from "./components/OnboardingModal";
import FundedCelebrationModal from "./components/FundedCelebrationModal";
import PaymentGraceModal from "../payment/PaymentGraceModal";
import PaymentGraceBanner from "../payment/PaymentGraceBanner";
import RiskDisclosure from "./components/legal/RiskDisclosure";
import { initPostHog, identifyUser, resetPostHog, trackPageview } from './utils/posthog';
import { useTradovateOAuthCompletion } from '../trading-accounts/tradovate/useTradovateOAuthCompletion';
import { captureAffiliateRefFromUrl } from './affiliateRef';
import { installChartErrorGuard } from './installChartErrorGuard';
import { installChunkReloadGuard } from './installChunkReloadGuard';
import { guardPortalNode } from './portalNodeGuard';
import { AppErrorBoundary } from './components/AppErrorBoundary';

// Recover from stale chunks after a deploy BEFORE anything else can surface the
// failure as a fatal render error.
installChunkReloadGuard();
// <body> héberge lui aussi des portails : le conteneur dédié y est ajouté, et
// Radix retombe sur <body> pour tout portail rendu avant sa création. Sans ce
// garde, la course React ↔ animation de sortie y lève `removeChild` en commit
// phase et React 19 démonte la page entière. Voir portalNodeGuard.ts.
// `typeof document` : ce module est aussi chargé côté serveur (SSR head/SEO).
if (typeof document !== "undefined") guardPortalNode(document.body);
// installChartErrorGuard s'enregistre AVANT initPostHog : les deux écoutent
// "error" en phase de capture, où l'ordre d'exécution suit l'ordre
// d'enregistrement. Installé après, il ne pourrait plus empêcher
// capture_exceptions de remonter la disposal-race de lightweight-charts.
installChartErrorGuard();
initPostHog();

function AppContent() {
  const location = useLocation();
  const t = useTranslation();
  const { data: user } = useAuth();
  const { data: currentUserRole } = useQuery(getCurrentUserRole, undefined, {
    enabled: Boolean(user),
  });
  const { data: affiliateProfile } = useQuery(getMyAffiliateProfile, undefined, {
    enabled: Boolean(user),
  });
  const hasSyncedAdminStatus = useRef(false);
  const hasIdentifiedPostHog = useRef(false);
  useTradovateOAuthCompletion(Boolean(user));

  const isMarketingPage = useMemo(
    () => isMarketingPath(location.pathname),
    [location],
  );

  // Marketing pages (landing + SEO pages) are locked to Classic Dark regardless
  // of the user's stored theme preference. We apply the "dark" body classes
  // imperatively here and restore the stored theme when leaving the marketing
  // zone. The user's preference itself is never overwritten.
  const [colorMode] = useColorMode();
  useEffect(() => {
    applyThemeClasses(isMarketingPage ? "dark" : colorMode);
  }, [isMarketingPage, colorMode]);

  const isAuthPage = useMemo(() => {
    return (
      location.pathname === routes.LoginRoute.build() ||
      location.pathname === routes.SignupRoute.build() ||
      location.pathname === routes.EmailVerificationRoute.build() ||
      location.pathname === routes.PasswordResetRoute.build() ||
      location.pathname === routes.RequestPasswordResetRoute.build()
    );
  }, [location]);

  const isAdminDashboard = useMemo(() => {
    return location.pathname.startsWith("/admin");
  }, [location]);

  const isSessionShare = useMemo(() => {
    return location.pathname.startsWith("/session/share/");
  }, [location]);

  const isAppPage = Boolean(user) && !isMarketingPage && !isAuthPage && !isAdminDashboard && !isSessionShare;

  const customPagesActions = useCustomPages();
  const uiMode = useUiMode();

  const navigationItems = useMemo(() => {
    return isMarketingPage
      ? getMarketingNavigationItems(t)
      : getDemoNavigationItems(t, user, Boolean(affiliateProfile));
  }, [isMarketingPage, t, user, affiliateProfile]);

  const effectiveUser = user
    ? ({ ...user, isAdmin: currentUserRole?.isAdmin ?? Boolean((user as any).isAdmin) } as any)
    : null;

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      const element = document.getElementById(id);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
      return;
    }
    // No anchor: reset scroll to the top on navigation so links that live at
    // the bottom of a page (e.g. the footer) don't land the user mid-page.
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (!user || hasSyncedAdminStatus.current) return;
    hasSyncedAdminStatus.current = true;
    syncMyAdminStatus().catch(() => {
      hasSyncedAdminStatus.current = false;
    });
  }, [user]);

  useEffect(() => {
    if (!user?.id) return;
    // Fire-and-forget presence ping. Uses a plain API route (not a Wasp
    // action) so the response doesn't invalidate every User-scoped query
    // — that was creating a hot request loop with useEffect([user]).
    //
    // Goes through Wasp's `api` axios instance: it already targets the
    // correct backend URL (config.apiUrl) and injects the Authorization
    // header from the stored session id. A hand-rolled fetch can't, because
    // the API base URL isn't reliably exposed as an env var in prod builds.
    const ping = () => {
      api.post("/api/ping-activity").catch(() => {});
    };
    ping();
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") {
        ping();
      }
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user?.id]);

  useEffect(() => {
    if (user && !hasIdentifiedPostHog.current) {
      hasIdentifiedPostHog.current = true;
      identifyUser({
        id: user.id,
        email: user.email ?? undefined,
        subscriptionPlan: (user as any).subscriptionPlan,
        subscriptionStatus: (user as any).subscriptionStatus,
        createdAt: (user as any).createdAt,
      });
    }
    if (!user) {
      hasIdentifiedPostHog.current = false;
      resetPostHog();
    }
  }, [user]);

  useEffect(() => {
    trackPageview(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    const utmSource = new URLSearchParams(window.location.search).get("utm_source");
    if (utmSource && !localStorage.getItem("edgely_utm_source")) {
      localStorage.setItem("edgely_utm_source", utmSource);
    }
    captureAffiliateRefFromUrl();
  }, []);

  let layout: JSX.Element;

  // Reset the boundary on navigation so a crashed page never traps the user.
  const pageBoundaryKey = location.pathname;

  if (isAdminDashboard) {
    layout = (
      <AppErrorBoundary key={pageBoundaryKey} source="admin-page">
        <Outlet />
      </AppErrorBoundary>
    );
  } else if (isAppPage && uiMode.mode === "lite") {
    // Mode Lite (accès par LITE_MODE_USERS, cf. useUiMode) : shell Lite. Les
    // pages communes basculent via src/lite/routes.tsx ; les autres pages
    // s'affichent telles quelles dans ce shell.
    layout = (
      <LiteShell user={effectiveUser ?? {}} banner={<PaymentGraceBanner />}>
        <AppErrorBoundary key={pageBoundaryKey} source="app-page">
          <Outlet />
        </AppErrorBoundary>
      </LiteShell>
    );
  } else if (isAppPage) {
    // Navigation à deux niveaux (rail + barre contextuelle) : la seule navigation
    // de l'app Advanced. L'ancienne barre latérale (AppSidebar) et son flag
    // REACT_APP_NEW_NAV_ENABLED ont été retirés le 2026-10-04 (flag à true en
    // prod comme en preprod depuis sa mise en service).
    layout = (
      <div className="flex bg-background text-foreground min-h-screen">
        <PrimaryRail user={effectiveUser ?? {}} />
        <SecondarySidebar
          customPages={customPagesActions.items ?? []}
          addPageSlot={
            <AddCustomPageButton
              onCreatePage={customPagesActions.onCreatePage}
              user={effectiveUser ?? {}}
            />
          }
          collapsedAddPageSlot={
            <AddCustomPageButton
              isCollapsed
              onCreatePage={customPagesActions.onCreatePage}
              user={effectiveUser ?? {}}
            />
          }
        />
        <div className="flex flex-col flex-1 min-w-0">
          <MobileNavigation2
            userPlan={(effectiveUser as any)?.subscriptionPlan ?? null}
            customPages={customPagesActions.items ?? []}
          />
          <PaymentGraceBanner />
          <main className="flex-1 w-full px-3 sm:px-5 py-4 pb-20 lg:pb-4">
            <AppErrorBoundary key={pageBoundaryKey} source="app-page">
              <Outlet />
            </AppErrorBoundary>
          </main>
          <OnboardingModal />
        </div>
      </div>
    );
  } else if (isSessionShare) {
    layout = (
      <AppErrorBoundary key={pageBoundaryKey} source="session-share">
        <Outlet />
      </AppErrorBoundary>
    );
  } else {
    layout = (
      <div className="bg-background text-foreground min-h-screen">
        {!isAuthPage && <NavBar navigationItems={navigationItems} customPages={customPagesActions} />}
        <div className="mx-auto max-w-screen-2xl px-3 sm:px-0">
          <AppErrorBoundary key={pageBoundaryKey} source="public-page">
            <Outlet />
          </AppErrorBoundary>
        </div>
        {!isAuthPage && (
          <footer className="py-4 text-center">
            <span className="font-bold text-sm" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              edgely<span className="text-teal-500">.</span>
            </span>
          </footer>
        )}
      </div>
    );
  }

  // The testimonials disclaimer is landing-page-only (exact "/" route); it rides
  // inside the same RiskDisclosure section, below the risk text, no divider.
  const isLandingPage = location.pathname === "/";

  // The risk disclosure is shown only on the landing page and other public
  // pages (marketing, blog, legal, auth, session-share). It is hidden inside
  // the authenticated app UI (dashboard, journal, admin, …).
  const showRiskDisclosure = !isAppPage && !isAdminDashboard;

  return (
    <>
      {layout}
      {isAppPage && <FundedCelebrationModal />}
      {/* Impaye : uniquement dans l'app authentifiee. Sur les pages publiques
          (landing, blog, tarifs) une modale de facturation n'a pas de sens. */}
      {isAppPage && <PaymentGraceModal />}
      {showRiskDisclosure && (
        <RiskDisclosure
          showTestimonialsDisclaimer={isLandingPage}
          showNinjaTraderNotice={isLandingPage}
        />
      )}
    </>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <LanguageProvider>
        <AppContent />
        <BotpressWidget />
        <Toaster position="bottom-right" />
        <CookieConsentBanner />
      </LanguageProvider>
    </HelmetProvider>
  );
}
